import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHotkeys } from 'react-hotkeys-hook';
import QBankPerformance from '../../widgets/QBankPerformance';
import { RootState } from '../../store/store';
import Heatmap from '../../widgets/Heatmap';
import DonutChart from 'react-donut-chart';
import { fetchVignetteCompletion } from '../../services/vignettes';
import { fetchFlashcardCompletion } from '../../services/flashcards';
import { UserExam } from '../../types/User';
import FlashcardTrend from '../../widgets/FlashcardTrend';
import Skeleton from '@mui/material/Skeleton';
import { useNavigate } from 'react-router-dom';
import Page from '../../components/Page';
import TopicPerformance from './TopicPerformance';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { Info } from 'react-feather';
import Tooltip from '@mui/material/Tooltip';
import WorkloadSelect from '../../components/Global/Header/WorkloadSelect';
import { useRefreshStudySessionAndContent } from '../../utils/refreshStudySession';
import { areAllValuesDefined } from '../../utils/miscUtils';

interface DashboardProps {}

const Dashboard: React.FC<DashboardProps> = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const [flashcardsSeen, setFlashcardsSeen] = useState(0);
  const [flashcardCompletionPercentage, setFlashcardCompletionPercentage] =
    useState(0);
  const [vignettesSeen, setVignettesSeen] = useState(0);
  const [vignettesSeenPercentage, setVignettesSeenPercentage] = useState(0);
  const [vignettesCorrect, setVignettesCorrect] = useState(0);
  const [vignetteCorrectPercentage, setVignetteCorrectPercentage] = useState(0);

  const {
    setIsLoadingStudySessionAndContent,
    setIsLoadedStudySessionAndContent,
    refreshStudySessionAndContent,
  } = useRefreshStudySessionAndContent();

  const navigate = useNavigate();

  useHotkeys(
    'esc',
    () => {
      navigate('/');
    },
    { keyup: true }
  );

  useEffect(() => {
    if (primaryExam) {
      calculateContentCompletion(primaryExam);
    }
  }, [primaryExam]);

  const calculateContentCompletion = async (primaryExam: UserExam) => {
    if (currentUser && primaryExam) {
      if (primaryExam.isUsingQbank) {
        fetchVignetteCompletion(currentUser.id, primaryExam.id).then(
          (response) => {
            if (response) {
              setVignettesSeen(response.seenVignetteCount);
              setVignettesSeenPercentage(response.percentSeen);
              setVignettesCorrect(response.correctVignetteCount);
              setVignetteCorrectPercentage(response.percentCorrect);
            }
          }
        );
      }
      if (primaryExam.isUsingFlashcards) {
        fetchFlashcardCompletion(currentUser.id, primaryExam.id).then(
          (response) => {
            if (response) {
              setFlashcardsSeen(response.seenFlashcardCount);
              setFlashcardCompletionPercentage(response.percentCompleted);
            }
          }
        );
      }
    }
  };

  return (
    <Page type="dashboard">
      <div className="dashboard">
        <div className="dashboard_wrapper">
          <div className={`dashboard_wrapper_header is-ready`}>
            <div className="exam-title">
              <span>{primaryExam?.name || ''}</span>
              <h1>Performance</h1>
            </div>
          </div>
          <div className={`dashboard_wrapper_body is-ready`}>
            <div className="dashboard_wrapper_body_main">
              {primaryExam?.isUsingQbank && <QBankPerformance />}
              {!primaryExam?.isUsingQbank && <FlashcardTrend />}
            </div>
            <div className="dashboard_wrapper_body_panel">
              <div className="block block--dashboard">
                <div className="block_body">
                  <h2 className="m-t-0">Content Completion</h2>
                  {primaryExam && (
                    <div className="mastery-rings">
                      {primaryExam.isUsingQbank && (
                        <>
                          {areAllValuesDefined(
                            vignettesSeen,
                            vignettesSeenPercentage,
                            vignettesCorrect,
                            vignetteCorrectPercentage,
                            primaryExam.selectedVignetteCount
                          ) && (
                            <div className="donut">
                              <DonutChart
                                height={120}
                                width={120}
                                data={[
                                  {
                                    label: 'Correct',
                                    value:
                                      primaryExam.type === 'CUSTOM'
                                        ? vignettesSeenPercentage
                                        : vignetteCorrectPercentage,
                                  },
                                  {
                                    label: '',
                                    value:
                                      primaryExam.type === 'CUSTOM'
                                        ? 100 - vignettesSeenPercentage
                                        : 100 - vignetteCorrectPercentage,
                                  },
                                ]}
                                colors={[
                                  'var(--green)',
                                  'var(--surface-tertiary)',
                                ]}
                                strokeColor={'transparent'}
                                innerRadius={0.75}
                                selectedOffset={0}
                                legend={false}
                                interactive={false}
                              />
                              <div className="donut_label">
                                <div className="donut_label_completed">
                                  {primaryExam.type === 'CUSTOM'
                                    ? vignettesSeen
                                    : vignettesCorrect}
                                </div>
                                <div className="donut_label_total">
                                  {primaryExam.selectedVignetteCount}
                                </div>
                              </div>
                              <div className="donut_outer-label">
                                QBank
                                <Tooltip
                                  title="QBank completion is based on the number of correctly answered vignettes."
                                  enterTouchDelay={0}
                                >
                                  <Info />
                                </Tooltip>
                              </div>
                            </div>
                          )}
                          {!areAllValuesDefined(
                            vignettesSeen,
                            vignettesSeenPercentage,
                            vignettesCorrect,
                            vignetteCorrectPercentage,
                            primaryExam.selectedVignetteCount
                          ) && (
                            <Skeleton
                              sx={{
                                bgcolor: 'rgba(255,255,255,.05)',
                                borderRadius: '1rem',
                              }}
                              variant="rectangular"
                              animation="wave"
                              width={120}
                              height={140}
                            />
                          )}
                        </>
                      )}
                      {primaryExam.isUsingFlashcards && (
                        <>
                          {areAllValuesDefined(
                            flashcardCompletionPercentage,
                            flashcardsSeen,
                            primaryExam.selectedFlashcardCount
                          ) && (
                            <div className="donut">
                              <DonutChart
                                height={120}
                                width={120}
                                data={[
                                  {
                                    label: 'Correct',
                                    value: flashcardCompletionPercentage,
                                  },
                                  {
                                    label: '',
                                    value: 100 - flashcardCompletionPercentage,
                                  },
                                ]}
                                colors={[
                                  'var(--green)',
                                  'var(--surface-tertiary)',
                                ]}
                                strokeColor={'transparent'}
                                innerRadius={0.75}
                                selectedOffset={0}
                                legend={false}
                                interactive={false}
                              />
                              <div className="donut_label">
                                <div className="donut_label_completed">
                                  {flashcardsSeen}
                                </div>
                                <div className="donut_label_total">
                                  {primaryExam.selectedFlashcardCount}
                                </div>
                              </div>
                              <div className="donut_outer-label">
                                Flashcards
                              </div>
                            </div>
                          )}
                          {!areAllValuesDefined(
                            flashcardCompletionPercentage,
                            flashcardsSeen,
                            primaryExam.selectedFlashcardCount
                          ) && (
                            <Skeleton
                              sx={{
                                bgcolor: 'rgba(255,255,255,.05)',
                                borderRadius: '1rem',
                              }}
                              variant="rectangular"
                              animation="wave"
                              width={120}
                              height={140}
                            />
                          )}
                        </>
                      )}
                      <WorkloadSelect
                        setIsLoadingStudySessionAndContent={
                          setIsLoadingStudySessionAndContent
                        }
                        setIsLoadedStudySessionAndContent={
                          setIsLoadedStudySessionAndContent
                        }
                        refreshStudySessionAndContent={
                          refreshStudySessionAndContent
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
              <Heatmap />
            </div>
          </div>
          <div
            className={`dashboard_wrapper_body dashboard_wrapper_body--topics is-ready`}
          >
            <div className="block block--dashboard block--dashboard--topics">
              <div className="block_body">
                <h2 className="m-t-0">
                  Topic Performance
                  <Tooltip
                    title="Topic performance is calculated across all exams."
                    enterTouchDelay={0}
                  >
                    <Info />
                  </Tooltip>
                </h2>
                <TopicPerformance />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default Dashboard;
